import QuantityItem from "./QuantityItem";

import Infoblock from "../InfoBlock";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../hooks/reduxHooks";
import {
  goToDesign,
  goToQuantity,
  goToReview,
} from "../../../store/slices/stepSlice";

import Popup from "../Popup";
import SaveDesign from "../SaveDesign";
import Cookies from "js-cookie";
import {
  checkAndCreateSessionIdFromCookie,
  getTempCart,
  saveSelectedColorsToCookie,
} from "../../../../utils/utils";
import {
  setRefresher,
  setTotalSelectedQuantity,
} from "../../../store/slices/productSlice";
import PricingCalculator from "./PricingCalculator";
import MobileBottomNav from "../MobileBottomNav";
import { useParams } from "react-router-dom";

// CSS styles for Buy More Save More section
const buyMoreStyles = {
  buyMore: {
    fontSize: "12px",
    margin: "16px 0 0",
    padding: "15px 8px",
    borderRadius: "4px",
    border: "1px solid #d0e9c6",
    backgroundColor: "#d4edda",
  },
  row: {
    display: "flex",
    marginTop: "8px",
  },
  col6: {
    flex: "0 0 50%",
  },
  textEnd: {
    textAlign: "right" as const,
  },
  col12: {
    flex: "0 0 100%",
    textAlign: "center" as const,
  },
};

// Tiered discount calculation function
const calculateDiscount = (totalQuantity: number): number => {
  if (totalQuantity >= 288) return 0.5; // Tier 6: 40% off for 144+
  if (totalQuantity >= 144) return 0.47; // Tier 6: 40% off for 144+
  if (totalQuantity >= 96) return 0.45; // Tier 5: 35% off for 96-143
  if (totalQuantity >= 72) return 0.4; // Tier 4: 25% off for 72-95
  if (totalQuantity >= 48) return 0.35; // Tier 3: 20% off for 48-71
  if (totalQuantity >= 24) return 0.3; // Tier 2: 15% off for 24-47
  if (totalQuantity >= 12) return 0.2; // Tier 1: 10% off for 12-23
  if (totalQuantity >= 6) return 0.1; // Tier 1: 10% off for 12-23
  return 0; // No discount for less than 12 items
};

const Pricing = () => {
  const selectedPrintMethod = useAppSelector(
    (state) => state.canvases.selectedPrintMethod
  );
  const { designId } = useParams(); // Extract `designId` from the URL
  const [showSaveDesign, setShowSaveDesign] = useState(false);
  const [showDiscountDetails, setShowDiscountDetails] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);

  const [items, setItems] = useState<any>([]);
  // We'll use localStorage instead of component state to persist quantities
  const [reviewAvailable, setReviewAvailable] = useState(false);
  const [totalQty, setTotalQty] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [originalPrice, setOriginalPrice] = useState(0);
  const currentStep = useAppSelector((state) => state.steps.step);
  const productData = useAppSelector(
    (state) => state.product.product_basic_details
  );
  const [selectedColors, setSelectedColors] = useState<any>(null);
  const variantData = useAppSelector((state) => state.product.product_colors);
  const productImages = useAppSelector((state) => state.product.product_images);
  const refresher = useAppSelector((state) => state.product.refresher);
  const dispatch = useAppDispatch();

  const product_details = useAppSelector(
    (state) => state.product.product_details
  );
  const MIN_REQUIRED_PER_ITEM = product_details?.data?.min_qty_required || 1;

  // Check if we're on desktop
  useEffect(() => {
    const checkIfDesktop = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };

    checkIfDesktop();
    window.addEventListener("resize", checkIfDesktop);

    return () => {
      window.removeEventListener("resize", checkIfDesktop);
    };
  }, []);

  const calculateTotalPrice = () => {
    // Calculate the original price without discount
    const originalTotal = items.reduce((acc: number, item: any) => {
      const itemTotal =
        item.qty.reduce((sum: number, qtyItem: any) => {
          return sum + qtyItem.quantity.selected;
        }, 0) * item.price;
      return acc + itemTotal;
    }, 0);

    setOriginalPrice(originalTotal);

    // Calculate discount based on total quantity
    const discount = calculateDiscount(totalQty);
    setDiscountPercentage(discount);

    // Apply discount to the total price
    const discountedTotal = originalTotal * (1 - discount);
    setTotalPrice(discountedTotal);
  };

  const calTotalQuantity = () => {
    const totalQuantity = items.reduce((acc: number, item: any) => {
      const itemQuantity = item.qty.reduce((sum: number, qtyItem: any) => {
        return sum + qtyItem.quantity.selected;
      }, 0);
      return acc + itemQuantity;
    }, 0);
    dispatch(setTotalSelectedQuantity(totalQuantity));
    setTotalQty(totalQuantity);
  };

  useEffect(() => {
    calTotalQuantity();
  }, [items]);

  useEffect(() => {
    calculateTotalPrice();
  }, [totalQty, items]);

  useEffect(() => {
    const cartData = getTempCart();
    if (cartData) {
      const { sessionId, selectedColors } = JSON.parse(cartData);
      // Optionally check if sessionId matches User_Session_ID
      setSelectedColors(selectedColors);
    }
  }, [refresher]);
  // Function to generate the qty array for different colors.
  const generateQty = (selected: any, quantity: any) => [
    { qty: selected, max: quantity },
  ];

  // Helper function to save quantities to localStorage
  const saveQuantitiesToLocalStorage = (
    color: string,
    sizeQuantities: Record<string, number>
  ) => {
    try {
      // Get existing quantities from localStorage
      const existingData = localStorage.getItem("deletedItemsQuantities");
      let quantities = existingData ? JSON.parse(existingData) : {};

      // Update with new quantities
      quantities[color] = sizeQuantities;

      // Save back to localStorage
      localStorage.setItem(
        "deletedItemsQuantities",
        JSON.stringify(quantities)
      );

      console.log(
        `Saved quantities for ${color} to localStorage:`,
        sizeQuantities
      );
    } catch (error) {
      console.error("Error saving quantities to localStorage:", error);
    }
  };

  // Helper function to get quantities from localStorage
  const getQuantitiesFromLocalStorage = (
    color: string
  ): Record<string, number> => {
    try {
      const data = localStorage.getItem("deletedItemsQuantities");
      if (!data) return {};

      const quantities = JSON.parse(data);
      return quantities[color] || {};
    } catch (error) {
      console.error("Error retrieving quantities from localStorage:", error);
      return {};
    }
  };

  const deleteNewColor = (id: any) => {
    console.log("Attempting to delete color:", id);

    // Find the item to be deleted
    const itemToDelete = items.find((item: any) => item.id === id);
    if (!itemToDelete) {
      console.log("Item not found for deletion");
      return;
    }

    // Store the quantities for this color
    const colorQuantities: Record<string, number> = {};

    // Extract quantities for each size
    itemToDelete.qty.forEach((qtyItem: any) => {
      colorQuantities[qtyItem.size] = qtyItem.quantity.selected;
    });

    // Save quantities to localStorage
    saveQuantitiesToLocalStorage(itemToDelete.color, colorQuantities);

    // Now proceed with the color deletion
    const newColors = selectedColors.filter(
      (selectedColor: any) => selectedColor !== id
    );

    console.log("New color list:", newColors);
    setSelectedColors(newColors);
    const uniqueId: any = checkAndCreateSessionIdFromCookie();
    saveSelectedColorsToCookie(
      uniqueId,
      newColors,
      product_details.CustomizedProductID
    );

    // Filter out the deleted item from items state
    const newItems = items.filter((item: any) => item.id !== id);
    setItems(newItems);

    // Trigger refresh after all state updates
    dispatch(setRefresher(!refresher));
  };

  // Mapping product and variant data to the desired structure.

  // Filter and set items based on selected colors
  useEffect(() => {
    if (productImages && variantData && productData) {
      const getImageUrlByVariantId = (variantId: number) => {
        const matchedItem = productImages.find(
          (item: any) => item.image_id === variantId
        );
        return matchedItem ? matchedItem.src : null;
      };

      // Filter variants based on selected colors from the cookie
      const filteredVariants =
        variantData?.catalogproductvariants.filter(
          (variant: any) =>
            selectedColors ? selectedColors.includes(variant.id) : false // Match color with cookie data
        ) || [];

      var defaultItems2 = filteredVariants.reduce(
        (acc: any[], variant: any) => {
          // Find the color group if it exists, or create a new one
          let colorGroup = acc.find((item) => item.color === variant.option1);

          if (!colorGroup) {
            // If no color group found, create a new one with the necessary fields
            colorGroup = {
              id: variant.id,
              title: productData.productName,
              image:
                getImageUrlByVariantId(variant.image_id) ||
                productData.productImage,
              color: variant.option1,
              qty: [], // Array to hold sizes and quantities
              price: parseFloat(variant.price),
              addMoreAmount: 0,
              addMoreSaveEach: 0,
            };
            acc.push(colorGroup);

            // Get stored quantities for this color from localStorage
            const storedQuantities = getQuantitiesFromLocalStorage(
              variant.option1
            );
            console.log(
              `Retrieved quantities for ${variant.option1} from localStorage:`,
              storedQuantities
            );

            // Add all sizes for this color with their quantities
            variantData?.catalogproductvariants.forEach((sizeVariant: any) => {
              if (sizeVariant.option1 === variant.option1) {
                // Check if we have a stored quantity for this size
                const storedQuantity =
                  storedQuantities[sizeVariant.option2 || ""] || 1;

                colorGroup.qty.push({
                  size: sizeVariant.option2 || "", // If there's no option2, use empty string
                  quantity: {
                    total: sizeVariant.inventory_quantity,
                    selected: storedQuantity, // Use stored quantity if available, otherwise default to 1
                  },
                });
              }
            });
          }

          return acc;
        },
        []
      );

      setItems(defaultItems2); // Set the filtered items in the state
    }
  }, [variantData, productData, productImages, selectedColors]);

  const handleSetQty = (
    id: any,
    color: string,
    size: string,
    quantity: number
  ) => {
    const updatedProducts = items.map((item: any, index: any) => {
      if (item.id !== id.id) return item; // Return unchanged item if ID doesn't match
      const updatedQty = item.qty.map((qtyItem: any, index2: any) => {
        if (qtyItem.size === size) {
          return {
            size: size,
            quantity: {
              total: qtyItem.quantity.total,
              selected: quantity,
            },
          }; // Update the quantity for the matching size
        }
        return qtyItem; // Return unchanged quantity for non-matching sizes
      });

      // Also update the localStorage with the new quantity
      const storedQuantities = getQuantitiesFromLocalStorage(color);
      storedQuantities[size] = quantity;
      saveQuantitiesToLocalStorage(color, storedQuantities);

      return { ...item, qty: updatedQty }; // Update the item with new quantities
    });

    setItems(updatedProducts);
  };

  const handleDeleteProduct = (id: any) => {
    // We'll use the deleteNewColor function which now handles both
    // storing quantities and removing the item
    deleteNewColor(id);
  };

  if (currentStep === 0) return null;

  const checkQuantity = (items: any, minRequired: number) => {
    return items.every(
      (item: any) =>
        item.qty.reduce(
          (total: number, qtyItem: any) => total + qtyItem.quantity.selected,
          0
        ) >= minRequired
    );
  };
  const findSKUForAddToCart = () => {
    const selectedProducts: any = [];

    variantData?.catalogproductvariants.map((variant: any) => {
      items.map((item: any) => {
        if (variant.option1 === item.color) {
          item.qty.map((qtyItem: any) => {
            if (
              qtyItem.size === variant.option2 &&
              qtyItem.quantity.selected > 0
            ) {
              selectedProducts.push({
                ...variant,
                quantity: qtyItem.quantity.selected,
              });
            }
          });
        }
      });
    });
    console.log("Selected Products:", selectedProducts);
    return selectedProducts;
  };

  function createCartItems(data: any[], designData: any) {
    return data.map((item) => {
      const options: Record<string, string> = {}; // Object to hold all options
      // Add all available options to the options object
      if (item.option1) options["option-0"] = item.option1;
      if (item.option2) options["option-1"] = item.option2;
      if (item.option3) options["option-2"] = item.option3;

      return {
        isNewDesign: false,
        id: item.shopify_variant_id, // Variant ID of the product
        quantity: item.quantity, // Quantity of the item to add
        "product-id": productData.shopify_product_id, // Product ID
        "section-id": "template--15468235587620__main", // Static section ID as required
        ...options, // Spread operator to add options
        customProperties: {
          // Additional properties to send with each item
          designImage: designData?.designImage,
          designSvg: designData?.designSvg,
          editDesignURL: designData?.editDesignURL,
          designId: designData?.designId,
          printMethodId: designData?.printMethodId,
          unique_id: designData?.unique_id,
          designPng: designData?.designPng,
        },
      };
    });
  }

  return (
    <div className="fixed z-10 max-lg:z-50 top-[80px] max-lg:top-0 right-0 w-full max-w-[500px] max-lg:max-w-none max-lg:pb-32 [height:calc(100vh-80px)] max-lg:h-screen bg-white p-5 overflow-y-auto">
      {currentStep === 1 && (
        <>
          <div className="lg:block hidden text-[10px] text-primaryred uppercase font-semibold my-2">
            Quantity & Sizes
          </div>
          <div className="flex gap-5 justify-between items-center my-2 pt-2 lg:pt-0">
            <div className="flex gap-3 items-center">
              <span
                onClick={() => dispatch(goToDesign())}
                className="cursor-pointer"
              >
                <svg
                  width="19"
                  height="14"
                  viewBox="0 0 19 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M17.4989 5.83273H3.98294L7.82449 1.99118C7.9328 1.88287 8.01872 1.75428 8.07734 1.61277C8.13595 1.47125 8.16612 1.31958 8.16612 1.1664C8.16612 1.01323 8.13595 0.861555 8.07734 0.720041C8.01872 0.578526 7.9328 0.449943 7.82449 0.341632C7.71618 0.233321 7.5876 0.147405 7.44608 0.0887872C7.30457 0.0301699 7.15289 -1.14124e-09 6.99972 0C6.84654 1.14124e-09 6.69487 0.0301699 6.55336 0.0887872C6.41184 0.147405 6.28326 0.233321 6.17495 0.341632L0.342043 6.17454C0.233614 6.28278 0.147593 6.41134 0.0889014 6.55287C0.0302099 6.69439 0 6.8461 0 6.99931C0 7.15252 0.0302099 7.30423 0.0889014 7.44575C0.147593 7.58727 0.233614 7.71584 0.342043 7.82408L6.17495 13.657C6.28303 13.7657 6.41154 13.852 6.55309 13.9108C6.69463 13.9697 6.84642 14 6.99972 14C7.15302 14 7.3048 13.9697 7.44635 13.9108C7.58789 13.852 7.7164 13.7657 7.82449 13.657C7.93292 13.5487 8.01894 13.4202 8.07763 13.2787C8.13632 13.1371 8.16653 12.9854 8.16653 12.8322C8.16653 12.679 8.13632 12.5273 8.07763 12.3858C8.01894 12.2442 7.93292 12.1157 7.82449 12.0074L3.98294 8.16589H17.4989C17.8083 8.16589 18.1051 8.04298 18.3238 7.8242C18.5426 7.60543 18.6655 7.3087 18.6655 6.99931C18.6655 6.68991 18.5426 6.39319 18.3238 6.17441C18.1051 5.95563 17.8083 5.83273 17.4989 5.83273Z"
                    fill="#424447"
                  />
                </svg>
              </span>
              <span className="text-2xl font-bold">
                Choose Quantity & Sizes
              </span>
            </div>
            <div
              onClick={() => dispatch(goToDesign())}
              className="cursor-pointer"
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 15 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15 1.5L13.5 0L7.5 6L1.5 0L0 1.5L6 7.5L0 13.5L1.5 15L7.5 9L13.5 15L15 13.5L9 7.5L15 1.5Z"
                  fill="black"
                />
              </svg>
            </div>
          </div>
          <div className="text-darkgrey text-sm my-3 pb-2 border-b border-solid border-lightgrey">
            Enter quantities to calculate the price. Save money by increasing
            quantity and reducing ink colors in your design.
          </div>

          {/* Volume Discount Information - Redesigned as a compact list with dropdown */}
          <div className="bg-green-50 rounded-lg mb-5 shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-3">
              <div
                className="flex items-center justify-between"
                onClick={() => setShowDiscountDetails(!showDiscountDetails)}
                style={{ cursor: "pointer" }}
              >
                <div className="flex items-center">
                  <svg
                    className="w-5 h-5 mr-2 text-green-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5 2a2 2 0 00-2 2v14l3.5-2 3.5 2 3.5-2 3.5 2V4a2 2 0 00-2-2H5zm4.707 3.707a1 1 0 00-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L8.414 9H10a3 3 0 013 3v1a1 1 0 102 0v-1a5 5 0 00-5-5H8.414l1.293-1.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-base font-bold text-gray-700">
                    Volume Discounts Available
                  </span>
                </div>

                {discountPercentage > 0 && (
                  <div className="bg-green-600 text-white rounded-full px-2 py-0.5 text-xs inline-flex items-center whitespace-nowrap">
                    <svg
                      className="w-3 h-3 mr-0.5 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="flex-shrink-0">
                      {(discountPercentage * 100).toFixed(0)}% off
                    </span>
                  </div>
                )}

                <svg
                  className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${
                    showDiscountDetails ? "transform rotate-180" : ""
                  }`}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>

              {/* Current discount status */}
              {items.length > 0 && totalQty > 0 && discountPercentage > 0 && (
                <div className="mt-2 flex items-center justify-between">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-green-600 h-2 rounded-full"
                      style={{ width: `${(discountPercentage / 0.35) * 100}%` }}
                    ></div>
                  </div>
                  <div className="ml-3 whitespace-nowrap text-green-700 font-bold text-sm">
                    Save ${(originalPrice - totalPrice).toFixed(2)}
                  </div>
                </div>
              )}

              {/* Dropdown content */}
              {showDiscountDetails && (
                <div className="mt-3 border-t border-green-100 pt-3">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-700">12-23 items</span>
                      <span className="text-green-600 font-bold">10% off</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-700">24-47 items</span>
                      <span className="text-green-600 font-bold">15% off</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-700">48-71 items</span>
                      <span className="text-green-600 font-bold">20% off</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-700">72-95 items</span>
                      <span className="text-green-600 font-bold">25% off</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-700">
                        96-143 items
                      </span>
                      <span className="text-green-600 font-bold">30% off</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-700">144+ items</span>
                      <span className="text-green-600 font-bold">35% off</span>
                    </div>
                  </div>

                  {items.length > 0 &&
                    totalQty > 0 &&
                    discountPercentage === 0 && (
                      <div className="mt-3 flex items-center text-green-700 bg-green-100 p-2 rounded text-sm">
                        <svg
                          className="w-4 h-4 mr-1 flex-shrink-0"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586L7.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.586V7z"
                            clipRule="evenodd"
                          />
                        </svg>
                        Add {12 - totalQty} more items to unlock a 10% discount!
                      </div>
                    )}
                </div>
              )}
            </div>
          </div>

          {!checkQuantity(items, MIN_REQUIRED_PER_ITEM) &&
          MIN_REQUIRED_PER_ITEM != 1 ? (
            <Infoblock type="error">
              <div className="flex justify-left items-center gap-5">
                <div>
                  <div className="text-sm">
                    Minimum quantity required: {MIN_REQUIRED_PER_ITEM} per item.
                  </div>
                </div>
              </div>
            </Infoblock>
          ) : (
            ""
          )}
          {items.length > 0 ? (
            <>
              {items.map((item: any) => (
                <QuantityItem
                  product={item}
                  key={item.id}
                  {...item}
                  item={item}
                  setQty={handleSetQty}
                  handleDeleteProduct={handleDeleteProduct}
                  reviewAvailable={reviewAvailable}
                  setReviewAvailable={setReviewAvailable}
                />
              ))}
              <div className="text-center hidden md:block">
                <button
                  onClick={() => dispatch(goToReview())}
                  className="my-6 rounded-full bg-primaryred text-white text-xl py-4 px-8 uppercase font-bold tracking-wide shadow-lg hover:shadow-xl transition-all transform hover:scale-105 disabled:opacity-60 disabled:hover:scale-100 disabled:hover:shadow-lg flex items-center justify-center mx-auto"
                  disabled={!checkQuantity(items, MIN_REQUIRED_PER_ITEM)}
                >
                  <svg
                    className="w-6 h-6 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-14a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V5a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Calculate Pricing
                </button>
              </div>
            </>
          ) : (
            <div className="my-10 text-lg text-center">No products added</div>
          )}
        </>
      )}
      {currentStep === 2 && checkQuantity(items, MIN_REQUIRED_PER_ITEM) ? (
        <>
          <div className="lg:block hidden text-[10px] text-primaryred uppercase font-semibold my-2">
            Review order
          </div>
          <div className="flex gap-5 justify-between items-center my-2 pt-2 lg:pt-0">
            <div className="flex gap-3 items-center">
              <span
                onClick={() => dispatch(goToQuantity())}
                className="cursor-pointer"
              >
                <svg
                  width="19"
                  height="14"
                  viewBox="0 0 19 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M17.4989 5.83273H3.98294L7.82449 1.99118C7.9328 1.88287 8.01872 1.75428 8.07734 1.61277C8.13595 1.47125 8.16612 1.31958 8.16612 1.1664C8.16612 1.01323 8.13595 0.861555 8.07734 0.720041C8.01872 0.578526 7.9328 0.449943 7.82449 0.341632C7.71618 0.233321 7.5876 0.147405 7.44608 0.0887872C7.30457 0.0301699 7.15289 -1.14124e-09 6.99972 0C6.84654 1.14124e-09 6.69487 0.0301699 6.55336 0.0887872C6.41184 0.147405 6.28326 0.233321 6.17495 0.341632L0.342043 6.17454C0.233614 6.28278 0.147593 6.41134 0.0889014 6.55287C0.0302099 6.69439 0 6.8461 0 6.99931C0 7.15252 0.0302099 7.30423 0.0889014 7.44575C0.147593 7.58727 0.233614 7.71584 0.342043 7.82408L6.17495 13.657C6.28303 13.7657 6.41154 13.852 6.55309 13.9108C6.69463 13.9697 6.84642 14 6.99972 14C7.15302 14 7.3048 13.9697 7.44635 13.9108C7.58789 13.852 7.7164 13.7657 7.82449 13.657C7.93292 13.5487 8.01894 13.4202 8.07763 13.2787C8.13632 13.1371 8.16653 12.9854 8.16653 12.8322C8.16653 12.679 8.13632 12.5273 8.07763 12.3858C8.01894 12.2442 7.93292 12.1157 7.82449 12.0074L3.98294 8.16589H17.4989C17.8083 8.16589 18.1051 8.04298 18.3238 7.8242C18.5426 7.60543 18.6655 7.3087 18.6655 6.99931C18.6655 6.68991 18.5426 6.39319 18.3238 6.17441C18.1051 5.95563 17.8083 5.83273 17.4989 5.83273Z"
                    fill="#424447"
                  />
                </svg>
              </span>
              <span className="text-2xl font-bold">
                Your Products & Pricing
              </span>
            </div>
            <div
              onClick={() => dispatch(goToDesign())}
              className="cursor-pointer"
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 15 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15 1.5L13.5 0L7.5 6L1.5 0L0 1.5L6 7.5L0 13.5L1.5 15L7.5 9L13.5 15L15 13.5L9 7.5L15 1.5Z"
                  fill="black"
                />
              </svg>
            </div>
          </div>
          <Infoblock type="info">
            <span className="text-xs">
              Your order includes a professional design review and satisfaction
              guarantee
            </span>
          </Infoblock>
          {items.map((item: any) => (
            <PricingCalculator
              key={item.id}
              {...item}
              handleDeleteProduct={handleDeleteProduct}
              discountPercentage={discountPercentage}
            />
          ))}

          {/* Total Price Display - Redesigned to be more compact */}
          <div className="bg-white rounded-lg overflow-hidden shadow-sm border border-gray-200 mt-6">
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-3 border-b border-gray-200">
              <div className="flex items-center">
                <svg
                  className="w-4 h-4 mr-2 text-gray-700"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 2a4 4 0 00-4 4v1H5a1 1 0 00-.994.89l-1 9A1 1 0 004 18h12a1 1 0 00.994-1.11l-1-9A1 1 0 0015 7h-1V6a4 4 0 00-4-4zm2 5V6a2 2 0 10-4 0v1h4zm-6 3a1 1 0 112 0 1 1 0 01-2 0zm7-1a1 1 0 100 2 1 1 0 000-2z"
                    clipRule="evenodd"
                  />
                </svg>
                <h3 className="text-sm font-bold text-gray-800">
                  Order Summary
                </h3>
              </div>
            </div>

            <div className="p-3">
              <div className="flex justify-between items-center text-xs text-gray-600 mb-2">
                <div>Subtotal ({totalQty} items):</div>
                <div>${originalPrice.toFixed(2)}</div>
              </div>

              {discountPercentage > 0 && (
                <div className="flex justify-between items-center text-xs text-green-600 mb-2">
                  <div className="flex items-center">
                    <svg
                      className="w-3 h-3 mr-1"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Volume Discount ({(discountPercentage * 100).toFixed(0)}%):
                  </div>
                  <div>-${(originalPrice - totalPrice).toFixed(2)}</div>
                </div>
              )}

              <div className="border-t border-gray-200 my-2"></div>

              <div className="flex justify-between items-center">
                <div className="text-sm font-bold text-gray-800">Total:</div>
                <div className="flex items-end">
                  {discountPercentage > 0 ? (
                    <div className="flex items-baseline">
                      <div className="line-through text-gray-400 text-xs mr-2">
                        ${originalPrice.toFixed(2)}
                      </div>
                      <div className="text-xl font-bold text-green-700">
                        ${totalPrice.toFixed(2)}
                      </div>
                    </div>
                  ) : (
                    <div className="text-xl font-bold text-gray-800">
                      ${totalPrice.toFixed(2)}
                    </div>
                  )}
                </div>
              </div>

              {discountPercentage > 0 && (
                <div className="mt-2 text-xs text-green-700 bg-green-50 p-2 rounded border border-green-100 flex items-center">
                  <svg
                    className="w-3 h-3 mr-1 flex-shrink-0 text-green-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5 2a2 2 0 00-2 2v14l3.5-2 3.5 2 3.5-2 3.5 2V4a2 2 0 00-2-2H5zm2.5 3a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm7 0a1.5 1.5 0 10-3 0 1.5 1.5 0 003 0zm-7 7a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm7 0a1.5 1.5 0 10-3 0 1.5 1.5 0 003 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="font-medium">
                    You save ${(originalPrice - totalPrice).toFixed(2)}
                  </span>{" "}
                  with our {(discountPercentage * 100).toFixed(0)}% discount
                  tier!
                </div>
              )}
            </div>
          </div>

          <div className="my-6 hidden md:block w-full">
            <button
              className="w-[90%] rounded-full bg-primaryred text-white text-xl py-4 uppercase font-bold tracking-wide shadow-lg hover:shadow-xl transition-all transform hover:scale-105 flex items-center justify-center mx-auto"
              onClick={() => {
                setShowSaveDesign(true);
              }}
            >
              <svg
                className="w-6 h-6 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
              </svg>
              Add to Cart
            </button>
          </div>

          {/* Buy More Save More section - Redesigned to be more compact */}
          {totalQty > 0 && currentStep === 2 && totalQty < 144 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mt-6">
              <div className="bg-gradient-to-r from-green-600 to-green-500 p-2 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <svg
                      className="w-4 h-4 mr-1"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M5 4a2 2 0 012-2h6a2 2 0 012 2v14l-5-2.5L5 18V4z" />
                    </svg>
                    <h3 className="font-bold text-sm">Buy More Save More!</h3>
                  </div>
                  <div className="text-xs bg-white bg-opacity-20 rounded-full px-2 py-0.5">
                    Unlock bigger discounts
                  </div>
                </div>
              </div>

              <div className="p-3">
                {(() => {
                  const currentPricePerItem = totalPrice / totalQty;
                  const originalPricePerItem = originalPrice / totalQty;

                  // Show only the next 2 available tiers or fewer if at higher tiers
                  let tiersToShow = [];

                  // Next tier: 24 items (15% discount)
                  if (totalQty < 24) {
                    const nextTierPrice = originalPricePerItem * 0.85;
                    const savingsPerItem = currentPricePerItem - nextTierPrice;
                    tiersToShow.push({
                      moreItems: 24 - totalQty,
                      savingsPerItem: savingsPerItem,
                      newPricePerItem: nextTierPrice,
                      discountPercent: 15,
                    });
                  }

                  // Next tier: 48 items (20% discount)
                  if (totalQty < 48) {
                    const nextTierPrice = originalPricePerItem * 0.8;
                    const savingsPerItem = currentPricePerItem - nextTierPrice;
                    tiersToShow.push({
                      moreItems: 48 - totalQty,
                      savingsPerItem: savingsPerItem,
                      newPricePerItem: nextTierPrice,
                      discountPercent: 20,
                    });
                  }

                  // Next tier: 72 items (25% discount)
                  if (totalQty < 72) {
                    const nextTierPrice = originalPricePerItem * 0.75;
                    const savingsPerItem = currentPricePerItem - nextTierPrice;
                    tiersToShow.push({
                      moreItems: 72 - totalQty,
                      savingsPerItem: savingsPerItem,
                      newPricePerItem: nextTierPrice,
                      discountPercent: 25,
                    });
                  }

                  // Next tier: 96 items (30% discount)
                  if (totalQty < 96) {
                    const nextTierPrice = originalPricePerItem * 0.7;
                    const savingsPerItem = currentPricePerItem - nextTierPrice;
                    tiersToShow.push({
                      moreItems: 96 - totalQty,
                      savingsPerItem: savingsPerItem,
                      newPricePerItem: nextTierPrice,
                      discountPercent: 30,
                    });
                  }

                  // Next tier: 144 items (35% discount)
                  if (totalQty < 144) {
                    const nextTierPrice = originalPricePerItem * 0.65;
                    const savingsPerItem = currentPricePerItem - nextTierPrice;
                    tiersToShow.push({
                      moreItems: 144 - totalQty,
                      savingsPerItem: savingsPerItem,
                      newPricePerItem: nextTierPrice,
                      discountPercent: 35,
                    });
                  }

                  // Limit to 2 tiers
                  tiersToShow = tiersToShow.slice(0, 2);

                  return (
                    <>
                      {tiersToShow.map((tier, index) => (
                        <div
                          key={index}
                          className="bg-gray-50 rounded p-2 mb-2 border border-gray-100"
                        >
                          <div className="flex justify-between items-center">
                            <div className="flex items-center">
                              <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-full w-6 h-6 flex items-center justify-center mr-2 text-white font-bold text-xs shadow-sm">
                                {tier.discountPercent}%
                              </div>
                              <div>
                                <div className="text-xs text-gray-800">
                                  Buy{" "}
                                  <span className="font-bold text-green-700">
                                    {tier.moreItems} more
                                  </span>{" "}
                                  items
                                </div>
                                <div className="text-xs text-gray-600">
                                  Save{" "}
                                  <span className="font-bold text-green-700">
                                    ${tier.savingsPerItem.toFixed(2)}
                                  </span>{" "}
                                  per item
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="line-through text-gray-400 text-xs">
                                ${currentPricePerItem.toFixed(2)}
                              </div>
                              <div className="font-bold text-green-700 text-sm">
                                ${tier.newPricePerItem.toFixed(2)} each
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}

                      {tiersToShow.length === 0 && totalQty >= 144 && (
                        <div className="bg-gray-50 rounded p-2 text-center border border-gray-100">
                          <svg
                            className="w-5 h-5 mx-auto text-green-600 mb-1"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <div className="text-sm font-bold text-green-700">
                            Maximum discount reached!
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          )}

          {/* <div className="text-right text-sm">
            Starting at $94/mo with Affirm.{" "}
            <a href="#" className="text-primaryred underline">
              Prequalify now
            </a>
          </div> */}
        </>
      ) : currentStep === 2 ? (
        <div>
          <div className="flex gap-5 justify-between items-center my-2 pt-2 lg:pt-0">
            <div className="flex gap-3 items-center">
              <span
                onClick={() => dispatch(goToQuantity())}
                className="cursor-pointer"
              >
                <svg
                  width="19"
                  height="14"
                  viewBox="0 0 19 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M17.4989 5.83273H3.98294L7.82449 1.99118C7.9328 1.88287 8.01872 1.75428 8.07734 1.61277C8.13595 1.47125 8.16612 1.31958 8.16612 1.1664C8.16612 1.01323 8.13595 0.861555 8.07734 0.720041C8.01872 0.578526 7.9328 0.449943 7.82449 0.341632C7.71618 0.233321 7.5876 0.147405 7.44608 0.0887872C7.30457 0.0301699 7.15289 -1.14124e-09 6.99972 0C6.84654 1.14124e-09 6.69487 0.0301699 6.55336 0.0887872C6.41184 0.147405 6.28326 0.233321 6.17495 0.341632L0.342043 6.17454C0.233614 6.28278 0.147593 6.41134 0.0889014 6.55287C0.0302099 6.69439 0 6.8461 0 6.99931C0 7.15252 0.0302099 7.30423 0.0889014 7.44575C0.147593 7.58727 0.233614 7.71584 0.342043 7.82408L6.17495 13.657C6.28303 13.7657 6.41154 13.852 6.55309 13.9108C6.69463 13.9697 6.84642 14 6.99972 14C7.15302 14 7.3048 13.9697 7.44635 13.9108C7.58789 13.852 7.7164 13.7657 7.82449 13.657C7.93292 13.5487 8.01894 13.4202 8.07763 13.2787C8.13632 13.1371 8.16653 12.9854 8.16653 12.8322C8.16653 12.679 8.13632 12.5273 8.07763 12.3858C8.01894 12.2442 7.93292 12.1157 7.82449 12.0074L3.98294 8.16589H17.4989C17.8083 8.16589 18.1051 8.04298 18.3238 7.8242C18.5426 7.60543 18.6655 7.3087 18.6655 6.99931C18.6655 6.68991 18.5426 6.39319 18.3238 6.17441C18.1051 5.95563 17.8083 5.83273 17.4989 5.83273Z"
                    fill="#424447"
                  />
                </svg>
              </span>
              <span className="text-2xl font-bold">Quantity Missing</span>
            </div>
            <div
              onClick={() => dispatch(goToQuantity())}
              className="cursor-pointer"
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 15 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15 1.5L13.5 0L7.5 6L1.5 0L0 1.5L6 7.5L0 13.5L1.5 15L7.5 9L13.5 15L15 13.5L9 7.5L15 1.5Z"
                  fill="black"
                />
              </svg>
            </div>
          </div>
          <Infoblock type="error">
            <div className="flex justify-left items-center gap-5">
              <div>
                <div className="text-sm">
                  Minimum quantity required: {MIN_REQUIRED_PER_ITEM} per item.
                  Please enter the quantity for each item before proceeding to
                  the next step. Click black arrow to go back.
                </div>
              </div>
            </div>
          </Infoblock>
        </div>
      ) : null}

      <Popup
        show={showSaveDesign}
        setShow={setShowSaveDesign}
        title="Save Design"
      >
        <SaveDesign
          isLoggedIn={false}
          isSaving={false}
          onSaveDesign={(saveDesignData: {
            email: string;
            designName: string;
          }) => {
            const skuData = findSKUForAddToCart();
            const designData = createCartItems(skuData, saveDesignData);
            const win: any = window;
            console.log(
              "Design Data:=============================",
              designData
            );
            const newDesignId = designData[0].customProperties.unique_id;
            const isNewDesign = designId !== newDesignId;
            designData[0].isNewDesign = isNewDesign;

            win.top.postMessage(designData, "*");
            setShowSaveDesign(false);
          }}
        />
      </Popup>

      {/* Add Mobile Bottom Navigation */}
      <MobileBottomNav
        currentStep={currentStep}
        totalQuantity={totalQty}
        totalPrice={totalPrice}
        discountPercentage={discountPercentage}
        checkQuantity={checkQuantity}
        minRequired={MIN_REQUIRED_PER_ITEM}
        items={items}
        setShowSaveDesign={setShowSaveDesign}
      />
    </div>
  );
};

export default Pricing;
